import { AlertCircle, Wallet } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@flexibilitypay/ui';
import { formatCurrency, formatDateTime } from '@flexibilitypay/shared';

import type { SaldoResponse } from '../../api/balance.api';

/**
 * (Backfill automático — agregar descripción)
 *
 * @taskId T058
 * @see functional/Specs/specs_cycle-2.md HU-27-BE
 * @see functional/Specs/specs_cycle-2.md HU-06-FE
 * @see functional/Specs/specs_cycle-2.md HU-13-FE
 */
interface BalanceCardProps {
  data: SaldoResponse | undefined;
  isError: boolean;
  errorMessage?: string | null;
}

function resolveLineaLabel(
  linea: string,
  t: (key: string) => string,
): string {
  const lower = linea?.toLowerCase?.() ?? '';
  if (lower.includes('disponible')) return t('balance:page.lineValues.disponible');
  if (lower.includes('contable')) return t('balance:page.lineValues.contable');
  return linea;
}

function toFormatCurrency(importe: number, moneda: string): string {
  const currency = moneda?.toUpperCase() === 'USD' ? 'USD' : 'ARS';
  return formatCurrency(String(importe), currency);
}

export function BalanceCard({
  data,
  isError,
  errorMessage,
}: BalanceCardProps) {
  const { t } = useTranslation(['balance']);

  return (
    <Card data-testid="balance-card">
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="flex size-9 items-center justify-center rounded-lg bg-emerald-500/10 text-emerald-600">
            <Wallet className="size-5" aria-hidden />
          </div>
          <div>
            <CardTitle className="text-base">{t('balance:page.title')}</CardTitle>
            <CardDescription>{t('balance:page.subtitle')}</CardDescription>
          </div>
        </div>
      </CardHeader>

      <CardContent>
        {isError && (
          <div
            role="alert"
            className="flex items-start gap-3 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive"
          >
            <AlertCircle className="size-4 mt-0.5 shrink-0" aria-hidden />
            <p className="font-medium">{errorMessage ?? t('balance:errors.generic')}</p>
          </div>
        )}

        {!isError && data && (
          <div className="space-y-4">
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">
                {resolveLineaLabel(data.linea, t)}
              </p>
              <p className="mt-1 text-3xl font-semibold tracking-tight">
                {toFormatCurrency(data.importe, data.moneda)}
              </p>
            </div>

            <dl className="grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
              <div>
                <dt className="text-muted-foreground">{t('balance:page.currency')}</dt>
                <dd className="font-medium">{data.moneda}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">{t('balance:page.line')}</dt>
                <dd className="font-medium">{data.linea}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">
                  {t('balance:page.lastUpdate')}
                </dt>
                <dd className="font-medium">{formatDateTime(data.fecha)}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">{t('balance:page.accountId')}</dt>
                <dd className="font-mono text-xs">{data.idCuenta}</dd>
              </div>
            </dl>
          </div>
        )}

        {!isError && !data && (
          <p className="text-sm text-muted-foreground">
            {t('balance:empty.noAccountSelected')}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
